import { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Card, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import { getDepartmentList } from "../../../services/Users-services";
import { prepareSiteList } from "../../../services/utilityFunctions";
import { useNavigate } from "react-router-dom";
import { filterStorageUtils } from "../../../services/filterStorageUtils";

export const MasterFilter = (props) => {
  const { showModal, closeModal } = props;
  const navigate = useNavigate();

  // State for actual filter data (from localStorage)
  const [_actualFilterData, setActualFilterData] = useState(
    filterStorageUtils.getFilterData()
  );
  const [_actualFilterList, setActualFilterList] = useState(
    filterStorageUtils.getFilterList()
  );

  // Local form state (for editing, not saved until Apply is clicked)
  const [_formFilterData, setFormFilterData] = useState(
    filterStorageUtils.getFilterData()
  );

  const [regionListUpdate, setRegionListUpdate] = useState(false);

  // Component state
  const [regionOptions, setRegionOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [siteOptionLoading, setSiteOptionLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentOptionLoading, setDepartmentOptionLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);

  // Update form data (local only, not saved to localStorage)
  const updateFormFilterData = useCallback((updater) => {
    setFormFilterData((prevData) => {
      const newData =
        typeof updater === "function" ? updater(prevData) : updater;
      return newData;
    });
  }, []);

  // Read fresh data from localStorage when modal opens
  useEffect(() => {
    if (showModal) {
      const freshData = filterStorageUtils.getFilterData();
      const freshList = filterStorageUtils.getFilterList();
      setActualFilterData(freshData);
      setActualFilterList(freshList);
      setFormFilterData(freshData); // Reset form to current saved state
      setErrors({}); // Clear any previous errors
    }
  }, [showModal]);

  // Listen for filter changes from other components (only for actual data)
  useEffect(() => {
    const unsubscribe = filterStorageUtils.subscribeToFilterChanges(() => {
      setActualFilterData(filterStorageUtils.getFilterData());
      setActualFilterList(filterStorageUtils.getFilterList());
    });

    return unsubscribe;
  }, []);

  // Initialize region list
  useEffect(() => {
    getRegionList();
  }, [regionListUpdate]);

  // Trigger table refresh when filters change
  const triggerTableRefresh = useCallback(() => {
    // Dispatch custom event to notify MasterTable
    window.dispatchEvent(
      new CustomEvent("masterTableRefresh", {
        detail: { timestamp: Date.now() },
      })
    );
  }, []);

  const getDepartmentOptions = async ({
    regionId = null,
    siteId = null,
    query = null,
  } = {}) => {
    setDepartmentOptionLoading(true);
    try {
      const res = await getDepartmentList({
        status: "Y",
        regionId: regionId == null ? _formFilterData.region.value : regionId,
        siteId: siteId == null ? _formFilterData.site.value : siteId,
        query: query,
      });

      if (res.status === "success") {
        const list = res.data.entityList.map((item) => ({
          value: item._ID,
          label: item.NAME,
        }));
        console.log("department list", list);
        setDepartmentOptions(list);
      }
    } catch (error) {
      console.error("Error fetching department options:", error);
    } finally {
      setDepartmentOptionLoading(false);
    }
  };

  const getRegionList = async () => {
    try {
      const list = JSON.parse(localStorage.getItem("regionList"));
      setRegionOptions(list || []);
    } catch (error) {
      console.error("Error getting region list:", error);
      setRegionOptions([]);
    }
  };

  const getSiteOptions = async (regionId, query = null) => {
    setSiteOptionLoading(true);
    try {
      if (regionId == null) return;
      const res = await prepareSiteList(regionId, navigate, query);
      if (res.length === 0) {
        setErrors({ site: "No site found for this region" });
      } else {
        setErrors(({ site, ...rest }) => rest);
      }
      setSiteOptions(res);
    } catch (error) {
      console.error("Error fetching site options:", error);
      setSiteOptions([]);
    } finally {
      setSiteOptionLoading(false);
    }
  };

  const handleSubmit = (e) => {
    const newErrors = {};

    // Date validation
    if (_formFilterData.date.status) {
      const { type, startDate, endDate } = _formFilterData.date;
      if (type === "BETWEEN" && (!startDate || !endDate)) {
        newErrors.date = "Please select both start and end dates.";
      } else if (type === "AFTER" && !startDate) {
        newErrors.date = "Please select a start date.";
      } else if (type === "BEFORE" && !endDate) {
        newErrors.date = "Please select an end date.";
      }
    }

    // Status validation
    if (
      _formFilterData.status.status &&
      _formFilterData.status.value === null
    ) {
      newErrors.status = "Please select a status.";
    }

    // Region validation
    if (
      _formFilterData.region.status &&
      _formFilterData.region.value === null
    ) {
      newErrors.region = "Please select a region.";
    }

    // Site validation
    if (_formFilterData.site.status && _formFilterData.site.value === null) {
      newErrors.site = "Please select a site.";
    }

    // Department validation
    if (
      _formFilterData.department.status &&
      _formFilterData.department.value === null
    ) {
      newErrors.department = "Please select a department.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Save to localStorage - this will trigger the table component's effect automatically
    filterStorageUtils.setFilterData(_formFilterData);

    console.log("Filters applied from master filter");
    closeModal();
  };

  const checkIfFiltersEmpty = () => {
    return !Object.entries(_formFilterData).some(
      ([key, value]) => value.status === true
    );
  };

  const handleTooltipClick = () => {
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 1000);
  };

  // Remove filter handler (works on form data only, not saved until Apply is clicked)
  const removeFilter = useCallback(
    (key) => {
      if (key === "region") {
        updateFormFilterData((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            status: false,
            label: null,
            value: null,
          },
          ["site"]: {
            ...prev["site"],
            status: false,
            label: null,
            value: null,
          },
        }));
      } else {
        updateFormFilterData((prev) => ({
          ...prev,
          [key]: { ...prev[key], status: false },
        }));
      }
      // Don't trigger table refresh here - only when Apply is clicked
    },
    [updateFormFilterData]
  );

  return (
    <Modal
      show={showModal}
      size="lg"
      onHide={(e) => {
        console.log("close modal");
        closeModal(); // Just close without applying
      }}
      centered
      scrollable
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="border-bottom pb-1">
        <div className="w-100">
          <Modal.Title className="mb-1">Advanced Filters</Modal.Title>
          <div
            className="d-flex flex-wrap gap-2"
            style={{
              minHeight: "32px",
              position: "relative",
              zIndex: 1,
            }}
          >
            {Object.entries(_formFilterData).map(([key, value], index) => {
              return value.status === false ? null : (
                <div
                  key={index}
                  className="d-flex align-items-center badge bg-primary-subtle text-primary rounded-pill"
                  style={{
                    flexShrink: 0,
                    border: "1px solid rgba(13, 110, 253, 0.4)",
                    padding: "4px 10px",
                    fontSize: "0.8rem",
                    fontWeight: "400",
                    boxShadow: "0 1px 3px rgba(13, 110, 253, 0.08)",
                    transition: "all 0.2s ease",
                    cursor: "default",
                  }}
                >
                  <span className="text-capitalize">{key}</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-1"
                    onClick={() => removeFilter(key)}
                    style={{
                      color: "#0d6efd",
                      opacity: 0.7,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <i
                      className="fas fa-times"
                      style={{ fontSize: "0.7rem" }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh" }}>
        <Form id="advancedFiltersForm" onSubmit={handleSubmit}>
          {_actualFilterList.date && (
            <Card className="mb-2">
              <Card.Header className="d-flex justify-content-between align-items-center py-2">
                <span>Date Filter</span>
                <Form.Check
                  type="switch"
                  checked={_formFilterData.date.status}
                  onChange={(e) => {
                    updateFormFilterData((prev) => ({
                      ...prev,
                      date: { ...prev.date, status: e.target.checked },
                    }));
                    setErrors((prev) => ({ ...prev, date: null }));
                  }}
                />
              </Card.Header>
              <Card.Body className="py-2">
                <Form.Group className="mb-2">
                  <Form.Label> Range </Form.Label>
                  <Select
                    options={[
                      { value: "BETWEEN", label: "Between Dates" },
                      { value: "AFTER", label: "From Date" },
                      { value: "BEFORE", label: "Till Date" },
                    ]}
                    value={
                      _formFilterData.date.status === false
                        ? null
                        : {
                            value: _formFilterData.date.type,
                            label:
                              _formFilterData.date.type === "BETWEEN"
                                ? "Between Dates"
                                : _formFilterData.date.type === "AFTER"
                                ? "From Date"
                                : "Till Date",
                          }
                    }
                    onChange={(option) => {
                      if (option !== null && option !== undefined) {
                        console.log("option", option);
                        updateFormFilterData(({ date, ...rest }) => ({
                          ...rest,
                          date: {
                            ...date,
                            type: option.value,
                          },
                        }));
                      }
                    }}
                    isDisabled={!_formFilterData.date.status}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        background: "white",
                        zIndex: 9999,
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? "#f0f0f0" : "white",
                        color: "#333",
                      }),
                    }}
                  />
                </Form.Group>
                <Row className="g-2">
                  {(_formFilterData.date.type === "AFTER" ||
                    _formFilterData.date.type === "BETWEEN") && (
                    <Col md={6}>
                      <Form.Group className="mb-2 mt-1">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            label="Start Date"
                            defaultValue={
                              _formFilterData.date.startDate != null
                                ? dayjs(_formFilterData.date.startDate)
                                : null
                            }
                            disabled={!_formFilterData.date.status}
                            onChange={(date) => {
                              if (!date) {
                                updateFormFilterData((prev) => ({
                                  ...prev,
                                  date: {
                                    ...prev.date,
                                    startDate: null,
                                  },
                                }));
                                return;
                              }

                              console.log("formattedDate", date);
                              updateFormFilterData((prev) => ({
                                ...prev,
                                date: {
                                  ...prev.date,
                                  startDate: date,
                                },
                              }));
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                style: { fontSize: "0.875rem" },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Form.Group>
                    </Col>
                  )}
                  {(_formFilterData.date.type === "BETWEEN" ||
                    _formFilterData.date.type === "BEFORE") && (
                    <Col md={6}>
                      <Form.Group className="mb-2 mt-1">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            label="End Date"
                            disabled={!_formFilterData.date.status}
                            defaultValue={
                              _formFilterData.date.endDate != null
                                ? dayjs(_formFilterData.date.endDate)
                                : null
                            }
                            onChange={(date) => {
                              if (!date) {
                                updateFormFilterData((prev) => ({
                                  ...prev,
                                  date: {
                                    ...prev.date,
                                    endDate: null,
                                  },
                                }));
                                return;
                              }

                              updateFormFilterData((prev) => ({
                                ...prev,
                                date: {
                                  ...prev.date,
                                  endDate: date,
                                },
                              }));
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                style: { fontSize: "0.875rem" },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
                {errors.date && (
                  <Form.Text className="text-danger d-block mt-1">
                    {errors.date}
                  </Form.Text>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Status Filter Card */}
          {_actualFilterList.status && (
            <Card className="mb-2">
              <Card.Header className="d-flex justify-content-between align-items-center py-2">
                <span>Status Filter</span>
                <Form.Check
                  type="switch"
                  checked={_formFilterData.status.status}
                  onChange={(e) => {
                    setErrors((prev) => ({ ...prev, status: null }));
                    if (e.target.checked) {
                      updateFormFilterData(({ status, ...rest }) => ({
                        ...rest,
                        status: {
                          ...status,
                          status: e.target.checked,
                        },
                      }));
                    } else {
                      updateFormFilterData(({ status, ...rest }) => ({
                        ...rest,
                        status: {
                          ...status,
                          status: e.target.checked,
                          value: null,
                        },
                      }));
                    }
                  }}
                />
              </Card.Header>
              <Card.Body className="py-2">
                <Form.Group>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      label="Active"
                      name="statusFilter"
                      disabled={!_formFilterData.status.status}
                      id="status-active"
                      checked={_formFilterData.status.value === "Y"}
                      onChange={(e) => {
                        updateFormFilterData(({ status, ...rest }) => ({
                          ...rest,
                          status: {
                            ...status,
                            value: "Y",
                          },
                        }));
                      }}
                      style={{ opacity: 1 }}
                      className="custom-radio"
                    />
                    <Form.Check
                      type="radio"
                      label="Inactive"
                      name="statusFilter"
                      id="status-inactive"
                      checked={_formFilterData.status.value === "N"}
                      onChange={() => {
                        updateFormFilterData(({ status, ...rest }) => ({
                          ...rest,
                          status: {
                            ...status,
                            value: "N",
                          },
                        }));
                      }}
                      style={{ opacity: 1 }}
                      className="custom-radio"
                      disabled={!_formFilterData.status.status}
                    />
                  </div>
                  <style>
                    {`
                    .custom-radio:disabled + .form-check-label {
                      opacity: 1 !important;
                      color: #212529 !important;
                    }
                    .custom-radio:disabled {
                      opacity: 0.5;
                    }
                    `}
                  </style>
                </Form.Group>
                {errors.status && (
                  <Form.Text className="text-danger d-block mt-1">
                    {errors.status}
                  </Form.Text>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Region Filter Card */}
          {_actualFilterList.region && (
            <Card className="mb-2">
              <Card.Header className="d-flex justify-content-between align-items-center py-2">
                <span>Region Filter</span>
                <Form.Check
                  type="switch"
                  checked={_formFilterData.region.status}
                  onChange={(e) => {
                    setErrors((prev) => ({ ...prev, region: null }));
                    if (!e.target.checked) {
                      updateFormFilterData(({ site, region, ...rest }) => ({
                        ...rest,
                        region: {
                          ...region,
                          value: null,
                          label: null,
                          status: e.target.checked,
                        },
                        site: {
                          ...site,
                          status: false,
                          value: null,
                          label: null,
                        },
                        department: {
                          ...rest.department,
                          value: null,
                          label: null,
                          status: false,
                        },
                      }));
                    } else {
                      updateFormFilterData(({ region, ...rest }) => ({
                        ...rest,
                        region: {
                          ...region,
                          status: e.target.checked,
                        },
                      }));
                    }
                  }}
                />
              </Card.Header>
              <Card.Body className="py-2">
                <Select
                  options={regionOptions}
                  value={_formFilterData.region}
                  onChange={(value) => {
                    if (value !== null && value !== undefined) {
                      updateFormFilterData(({ region, site, ...rest }) => ({
                        ...rest,
                        region: {
                          ...region,
                          value: value.value,
                          label: value.label,
                        },
                        site: {
                          ...site,
                          value: null,
                          label: null,
                        },
                      }));
                      if (_formFilterData.department.status === true)
                        getDepartmentOptions({ regionId: value.value });
                      if (_formFilterData.site.status === true)
                        getSiteOptions(value.value);
                    }
                  }}
                  isSearchable
                  isDisabled={!_formFilterData.region.status}
                  placeholder="Select regions..."
                />
                {errors.region && (
                  <Form.Text className="text-danger d-block mt-1">
                    {errors.region}
                  </Form.Text>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Site Filter Card */}
          {_actualFilterList.site && (
            <Card className="mb-2">
              <Card.Header className="d-flex justify-content-between align-items-center py-2">
                <span>Site Filter</span>
                <div className="position-relative">
                  <Form.Check
                    type="switch"
                    checked={_formFilterData.site.status}
                    onChange={(e) => {
                      if (_formFilterData.region.status === false) {
                        handleTooltipClick();
                        return;
                      }
                      if (!e.target.checked) {
                        setErrors(({ site, ...rest }) => rest);
                        updateFormFilterData(({ site, ...rest }) => ({
                          ...rest,
                          site: {
                            ...site,
                            status: false,
                            value: null,
                            label: null,
                          },
                          department: {
                            ...rest.department,
                            value: null,
                            label: null,
                            status: false,
                          },
                        }));
                      } else {
                        updateFormFilterData(({ site, ...rest }) => ({
                          ...rest,
                          site: {
                            ...site,
                            status: e.target.checked,
                          },
                        }));
                      }

                      if (
                        _formFilterData.region.value != null &&
                        e.target.checked
                      ) {
                        getSiteOptions(_formFilterData.region.value);
                      }
                    }}
                  />
                  {showTooltip && (
                    <div
                      className="position-absolute text-white px-2 py-1 rounded"
                      style={{
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        fontSize: "0.8rem",
                        width: "150px",
                        top: "-30px",
                        right: "0",
                        zIndex: 1000,
                        transition: "opacity 0.2s",
                        opacity: showTooltip ? 1 : 0,
                      }}
                    >
                      Please select region first
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="py-2">
                <Select
                  options={siteOptions}
                  value={_formFilterData.site}
                  onChange={(value) => {
                    if (value !== null && value !== undefined) {
                      updateFormFilterData(({ site, ...rest }) => ({
                        ...rest,
                        site: {
                          ...site,
                          value: value.value,
                          label: value.label,
                        },
                      }));
                      if (_formFilterData.department.status === true)
                        getDepartmentOptions({ siteId: value.value });
                    }
                  }}
                  components={
                    siteOptionLoading
                      ? {
                          DropdownIndicator,
                        }
                      : null
                  }
                  onInputChange={(query) => {
                    getSiteOptions(_formFilterData.region.value, query);
                  }}
                  isSearchable
                  isDisabled={!_formFilterData.site.status}
                  placeholder="Select site..."
                />
                {errors.site && (
                  <Form.Text className="text-danger d-block mt-1">
                    {errors.site}
                  </Form.Text>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Department Filter Card */}
          {_actualFilterList.department && (
            <Card className="mb-2">
              <Card.Header className="d-flex justify-content-between align-items-center py-2">
                <span>Department Filter</span>
                <Form.Check
                  type="switch"
                  checked={_formFilterData.department.status}
                  onChange={(e) => {
                    setErrors((prev) => ({ ...prev, department: null }));
                    if (!e.target.checked) {
                      updateFormFilterData(({ department, ...rest }) => ({
                        ...rest,
                        department: {
                          ...department,
                          value: null,
                          label: null,
                          status: e.target.checked,
                        },
                      }));
                    } else {
                      getDepartmentOptions();
                      updateFormFilterData(({ department, ...rest }) => ({
                        ...rest,
                        department: {
                          ...department,
                          status: e.target.checked,
                        },
                      }));
                    }
                  }}
                />
              </Card.Header>
              <Card.Body className="py-2">
                <Select
                  options={departmentOptions}
                  value={_formFilterData.department}
                  onChange={(value) => {
                    if (value !== null && value !== undefined)
                      updateFormFilterData(({ department, ...rest }) => ({
                        ...rest,
                        department: {
                          ...department,
                          value: value.value,
                          label: value.label,
                        },
                      }));
                  }}
                  onInputChange={(query) => {
                    if (!departmentOptionLoading)
                      getDepartmentOptions({ query });
                  }}
                  components={
                    departmentOptionLoading
                      ? {
                          DropdownIndicator,
                        }
                      : null
                  }
                  isSearchable
                  isDisabled={!_formFilterData.department.status}
                  placeholder="Select department..."
                />
                {errors.department && (
                  <Form.Text className="text-danger d-block mt-1">
                    {errors.department}
                  </Form.Text>
                )}
              </Card.Body>
            </Card>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="pt-2">
        <Button
          variant="secondary"
          onClick={() => {
            closeModal();
          }}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            handleSubmit();
          }}
          form="advancedFiltersForm"
        >
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const DropdownIndicator = (props) => {
  return (
    <div {...props.innerProps}>
      <div
        className="spinner-border text-primary spinner-border-sm m-2"
        role="status"
      ></div>
    </div>
  );
};
